// @flow

import React, { PureComponent } from 'react';
import { graphql, QueryRenderer } from 'react-relay';
import type { RouterHistory } from 'react-router';
import styled from 'styled-components';
import { i18n } from 'shared/utils';
import relayEnvironment from 'shared/gql/relayEnvironment';
import Panel from 'shared/components/common/Panel';
import Button from 'shared/components/common/Button';
import Icon from 'shared/components/common/Icon';
import GenerateAllPendingJobsMutation from 'main-app/mutations/GenerateAllPendingJobs';
import * as Actions from 'main-app/store/Actions';

type Props = {
  history: RouterHistory,
};

const WidgetContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 32px 24px;
  gap: 24px;
`;

const LargeNumber = styled.div`
  font-size: 64px;
  font-weight: 700;
  color: ${props => {
    if (props.count === 0) return '#4caf50';
    if (props.hasShortfalls) return '#f59e0b';
    return '#2196f3';
  }};
  display: flex;
  align-items: center;
  gap: 16px;
`;

const IconWrapper = styled.div`
  width: 64px;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background-color: ${props => {
    if (props.count === 0) return '#e8f5e9';
    if (props.hasShortfalls) return '#fff3e0';
    return '#e3f2fd';
  }};
  color: ${props => {
    if (props.count === 0) return '#4caf50';
    if (props.hasShortfalls) return '#f59e0b';
    return '#2196f3';
  }};
`;

const Description = styled.div`
  font-size: 16px;
  color: #666;
  text-align: center;
`;

const StatusIndicator = styled.div`
  display: flex;
  gap: 16px;
  font-size: 14px;
  color: #666;
`;

const StatusItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const StatusDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${props => props.color};
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 12px;
  width: 100%;
  justify-content: center;
`;

class JobGenerationWidget extends PureComponent<Props> {
  handleGenerateAll = (pendingOrders: Array<any>) => {
    if (!pendingOrders || pendingOrders.length === 0) {
      Actions.alertNotification(
        i18n.t('No pending orders to generate jobs for'),
        'warning'
      );
      return;
    }

    const totalPending = pendingOrders.reduce((sum, order) => sum + order.pendingCount, 0);
    const confirmMessage = i18n.t(
      `This will generate ${totalPending} job ticket(s) from ${pendingOrders.length} order(s). Continue?`
    );

    if (!window.confirm(confirmMessage)) {
      return;
    }

    // Show loading notification
    Actions.alertNotification(
      i18n.t('Generating job tickets...'),
      'info'
    );

    GenerateAllPendingJobsMutation.commit(
      {},
      {
        onSuccess: response => {
          const result = response?.generateAllPendingJobs?.result;
          const count = result?.success || 0;
          const errors = result?.failed || 0;
          const skipped = result?.skipped || 0;
          
          if (count > 0) {
            const message = errors > 0 || skipped > 0
              ? i18n.t(`Generated ${count} job(s). ${errors} error(s), ${skipped} skipped`)
              : i18n.t(`Successfully generated ${count} job ticket(s)!`);
            
            Actions.alertNotification(message, errors > 0 ? 'warning' : 'success');
          } else {
            Actions.alertNotification(
              i18n.t('No jobs were generated. Check that items have default workflows assigned.'),
              'warning'
            );
          }
        },
        onFailure: error => {
          Actions.alertNotification(
            i18n.t(`Failed to generate jobs: ${error.message || 'Unknown error'}`),
            'error'
          );
        },
      }
    );
  };

  render() {
    const { history } = this.props;

    return (
      <QueryRenderer
        environment={relayEnvironment}
        query={graphql`
          query JobGenerationWidgetQuery {
            ordersPendingJobGeneration {
              order {
                id
              }
              pendingCount
            }
          }
        `}
        render={({ props, error }) => {
          if (error) {
            return (
              <Panel
                title={i18n.t('Job Generation')}
                style={{ marginBottom: 24 }}
              >
                <WidgetContent>
                  <Description>{i18n.t('Error loading data')}</Description>
                </WidgetContent>
              </Panel>
            );
          }

          const orders = props?.ordersPendingJobGeneration || [];
          const count = orders.length;
          const totalPendingJobs = orders.reduce((sum, order) => sum + order.pendingCount, 0);
          
          const hasShortfalls = false; // We don't have material status in current schema
          const hasPending = count > 0;

          return (
            <Panel
              title={i18n.t('Job Generation')}
              style={{ marginBottom: 24 }}
            >
              <WidgetContent>
                <LargeNumber count={count} hasShortfalls={hasShortfalls}>
                  <IconWrapper count={count} hasShortfalls={hasShortfalls}>
                    <Icon
                      type={
                        !hasPending
                          ? 'circle-check-green'
                          : 'circle-info'
                      }
                      size={32}
                    />
                  </IconWrapper>
                  {count}
                </LargeNumber>
                
                <Description>
                  {hasPending
                    ? i18n.t(`${totalPendingJobs} job tickets pending generation from ${count} orders`)
                    : i18n.t('All orders have jobs created')
                  }
                </Description>

                <ActionButtons>
                  <Button
                    buttonType="secondary"
                    onClick={() => history.push('/jobs/pending-orders')}
                  >
                    {i18n.t('View Details')}
                  </Button>
                  {hasPending && (
                    <Button
                      buttonType="primary"
                      onClick={() => this.handleGenerateAll(orders)}
                    >
                      {i18n.t('Generate All Jobs')}
                    </Button>
                  )}
                </ActionButtons>
              </WidgetContent>
            </Panel>
          );
        }}
      />
    );
  }
}

export default JobGenerationWidget;
